import { extendTheme, type ThemeConfig } from '@chakra-ui/react';
import { colors } from './colors';
import { semanticTokens } from './semanticTokens';
import { components } from './components';
import { fonts } from './fonts';
import { styles } from './styles';

/**
 * Custom Chakra UI theme for Bower.
 *
 * Two-palette colour system (see theme/colors.ts):
 *   • brand.*  — workhorse identity color (the 2026-08 twilight blue). CTAs,
 *                links, active states, brand-tinted surfaces.
 *   • accent.* — deliberate-moment color (the same twilight today; kept as a
 *                separate key so the two can diverge). AI / sparkles /
 *                selection highlights.
 *
 * The brand colour is split BY ROLE, not by shade preference: the twilight
 * clears 3:1 (large text, fills, borders) but not 4.5:1, so body-size brand
 * text and any fill carrying a white label resolve to the navy `accent.700`
 * instead. See theme/colors.brand.ts.
 *
 * Ink is the warm `neutral.*` ramp so a brand repaint never tints body copy. Semantic tokens in `semanticTokens.colors` resolve everything
 * else (bg.surface, text.secondary, border.default, etc.) with light/dark
 * pairs.
 *
 * Typography (2026-08 brand identity): Suisse Int'l for body / GT Ultra
 * Median for headings, display moments and the wordmark / GT Pressura Mono for
 * tabular numerics, labels and code. See theme/fonts.brand.ts for the role
 * mapping and the licensed-weight constraints that set the heading weights.
 */

// Theme configuration
const config: ThemeConfig = {
  initialColorMode: 'light',
  // OFF on purpose. With this on, Chakra's provider arms a
  // `prefers-color-scheme` listener unconditionally and persists every OS
  // flip to `chakra-ui-color-mode`, so an explicit "Light" / "Dark" choice in
  // Settings was silently overwritten the next time the OS scheme changed.
  // `theme/ColorModePreferenceSync` owns OS-following instead, and only
  // while the user's preference is "System".
  useSystemColorMode: false,
  disableTransitionOnChange: false,
};

// Breakpoints for responsive design (mobile-first)
const breakpoints = {
  base: '0em', // 0px
  sm: '30em', // ~480px
  md: '48em', // ~768px
  lg: '62em', // ~992px
  xl: '80em', // ~1280px
  '2xl': '96em', // ~1536px
};

// Spacing scale
const space = {
  px: '1px',
  0.5: '0.125rem', // 2px
  1: '0.25rem', // 4px
  1.5: '0.375rem', // 6px
  2: '0.5rem', // 8px
  2.5: '0.625rem', // 10px
  3: '0.75rem', // 12px
  3.5: '0.875rem', // 14px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  7: '1.75rem', // 28px
  8: '2rem', // 32px
  9: '2.25rem', // 36px
  10: '2.5rem', // 40px
  12: '3rem', // 48px
  14: '3.5rem', // 56px
  16: '4rem', // 64px
  20: '5rem', // 80px
  24: '6rem', // 96px
  28: '7rem', // 112px
  32: '8rem', // 128px
  36: '9rem', // 144px
  40: '10rem', // 160px
  44: '11rem', // 176px
  48: '12rem', // 192px
  52: '13rem', // 208px
  56: '14rem', // 224px
  60: '15rem', // 240px
  64: '16rem', // 256px
  72: '18rem', // 288px
  80: '20rem', // 320px
  96: '24rem', // 384px
};

// Shadow system
/**
 * DESIGN.md elevation: each level pairs a tight "contact" shadow with a softer
 * "ambient" one, tinted with Charcoal 500 (33, 39, 33) rather than pure black —
 * a neutral-black shadow reads cold against the warm parchment canvas.
 *
 * The spec defines three levels (sm / md / lg). `xs`, `base`, `xl` and `2xl`
 * are kept so existing `boxShadow` props still resolve, mapped onto the nearest
 * spec level rather than left on the old black ramp.
 */
const shadows = {
  xs: '0 0 0 1px rgba(33, 39, 33, 0.05)',
  sm: '0 1px 2px rgba(33, 39, 33, 0.06), 0 1px 1px rgba(33, 39, 33, 0.04)',
  base: '0 1px 2px rgba(33, 39, 33, 0.06), 0 1px 1px rgba(33, 39, 33, 0.04)',
  md: '0 4px 8px rgba(33, 39, 33, 0.08), 0 2px 4px rgba(33, 39, 33, 0.05)',
  lg: '0 12px 24px rgba(33, 39, 33, 0.10), 0 4px 8px rgba(33, 39, 33, 0.06)',
  xl: '0 12px 24px rgba(33, 39, 33, 0.10), 0 4px 8px rgba(33, 39, 33, 0.06)',
  '2xl': '0 12px 24px rgba(33, 39, 33, 0.10), 0 4px 8px rgba(33, 39, 33, 0.06)',
  inner: 'inset 0 2px 4px 0 rgba(33, 39, 33, 0.06)',
  // Keyboard focus ring. rgba mirrors `accent.600` (#4D72B7) — NOT the
  // twilight 500 the rest of the accent role uses.
  //
  // 1.4.11 holds a focus indicator to 3:1 against every surface it can be drawn
  // on. Recomputed 2026-09-16 against the tokens as they resolve AFTER the
  // 2026-09 frame flip — `bg.surface` is parchment now, `sidebar.bg` is white
  // — because the previous table was measured on the pre-flip values and had
  // every number attached to the wrong surface:
  //
  //                                      accent.500   accent.600
  //   sidebar.bg / frame.panel  #FFFFFF     3.51         4.77
  //   bg.canvas / bg.surface    #F8F7F4     3.27         4.45
  //   frame.bg (page frame)     #F0EEE9     3.02         4.11
  //   bg.subtle                 #E5E3DE     2.73         3.72
  //   sidebar.item.hover        #E0DDD6     2.59         3.51
  //   bg.hover                  #DBDCDB     2.55         3.47
  //   sidebar.item.active       #D5D2CA     2.32         3.15
  //   bg.active                 #D0D1CE     2.29         3.11
  //
  // accent.500 clears 3:1 on white alone and fails on every tint the nav rail
  // and workspace drawer actually paint. accent.600 clears it on all eight.
  //
  // Keep this in step with the `border.focus` token below — same colour, two
  // delivery mechanisms (box-shadow vs borderColor). Pinned by
  // brandContrast.test.ts.
  outline: '0 0 0 var(--chakra-borderWidths-interactive) rgba(77, 114, 183, 0.95)',
  // Same ring, for a surface whose backdrop is NOT a flat token colour — today
  // that means the onboarding shape field, where controls sit straight on an
  // animating gradient rather than on a card.
  //
  // The reasoning above enumerates the surfaces the ring can be drawn on and
  // tunes accent.600 to clear 3:1 on each. A painted backdrop is not in that
  // enumeration and cannot be: its colour changes per pixel and per second.
  // Measured over the field at the shader's own intensity cap, the plain ring
  // falls to 2.19:1 in dark mode (light holds at 3.51).
  //
  // So give the ring a known neighbour instead of a better colour: an opaque
  // `bg.canvas` band is drawn first, and the ring sits outside it. 1.4.11 is a
  // contrast requirement against ADJACENT colour, and the adjacent colour is
  // now a token again — 3.73:1 dark, 4.45:1 light, on any backdrop whatsoever.
  outlineOnPattern: [
    '0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-bg-canvas)',
    '0 0 0 calc(var(--chakra-borderWidths-interactive) * 2) rgba(77, 114, 183, 0.95)',
  ].join(', '),
  // The INSET twin of `outlineOnPattern`, same two-band trick and the same
  // reason, for a control that cannot wear an outset ring at all.
  //
  // A protocol step's media tile is the case: the card is `overflow: hidden`,
  // so an outset ring is clipped away on every edge. An inset `box-shadow` on
  // the control is NOT the answer either - it paints below the control's
  // children, and the tile's frame is an opaque, full-bleed, positioned child,
  // so the ring is painted and then covered. It has to go on a pseudo-element,
  // which is generated after the children and composites above them; this
  // token is the paint, `_after` is the layer.
  //
  // Two bands for the same reason as `outlineOnPattern`: once a thumbnail
  // loads, the inner band lands on a PHOTOGRAPH, whose colour changes per
  // pixel. The opaque `bg.canvas` band gives the ring a known neighbour, so
  // 1.4.11's contrast-against-adjacent-colour holds on any image whatsoever.
  outlineOnPatternInset: [
    'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-bg-canvas)',
    'inset 0 0 0 calc(var(--chakra-borderWidths-interactive) * 2) rgba(77, 114, 183, 0.95)',
  ].join(', '),
  // Subtle "lift" used on selected pills in Tabs.pills + SegmentedControl so
  // they pop against the pale container. Keep the two in sync via this token.
  pill: '0 1px 2px rgba(15, 23, 42, 0.08)',
  none: 'none',
};

// Keyboard-focus affordances share one adjustable weight. Refer to this token
// instead of using 1px/2px literals in interactive states.
//
// 3px since the 2026-09 design pass, which asked for a chunkier ring.
//
// WIDENING IS ONLY FREE FOR A RING. An earlier version of this block claimed
// all 15 consumers were `_focusVisible` outlines or inset box-shadows and that
// this had been "verified before changing". It had not: `PrivacyPickerCard`
// and `AskBirdStep` read it as `borderWidth`, i.e. a RESTING border that is
// always painted, so 1px -> 3px tripled the outline on two selectable cards
// nobody meant to restyle. They now take `selectable` below. The token's name
// invites exactly that misreading, which is why the census matters more here
// than the value does — and why `interactionBorderWidth.test.ts` now checks
// the PROP FORM at the call sites, not just the number.
//
// The 15 was never recounted either. Recounted 2026-10-31:
// `--chakra-borderWidths-interactive` appears in 21 non-test files, 27
// occurrences — 19 components/pages at one each, `theme/components.ts` twice,
// and this file six times (its ring shadows, plus this comment). All are an
// `outline` shorthand or an inset `box-shadow`; no `borderWidth`
// reader is left. Recount with a plain grep before quoting this, and move the
// number here when you do — a stale census is what caused both regressions.
//
// The COLOUR deliberately did not change. The design sheet asked for Twilight
// 200, which measures 1.42:1 on white and 1.10:1 on `bg.subtle` — WCAG 2.2
// 1.4.11 wants 3:1 against every surface a ring is drawn on, so it would have
// left keyboard users with no visible indicator. `accent.600` clears it on all
// eight of them; the measured table is on the `outline` shadow above. Width
// was what the design actually wanted.
const borderWidths = {
  /** Focus rings ONLY — outlines and inset shadows, never a painted border. */
  interactive: '3px',
  /**
   * A resting border on a selectable card. Separate from `interactive` because
   * it IS layout: it is painted at rest, so its width moves the content inside.
   */
  selectable: '1px',
};

// Border radius scale
/**
 * Corner radius.
 *
 * REVERTED from the DESIGN.md doubling scale (4/8/16/32) to the values that
 * shipped before the rebrand. The spec's scale suits marketing surfaces, but
 * applied to product chrome it inflated every rounded edge at once — buttons,
 * suggestion chips, popovers, menus — and the app read as a softer, different
 * product rather than the same one rebranded. Colour and type carry the
 * identity here; the geometry stays put.
 *
 * If the spec scale is revisited, move it surface by surface. Changing the
 * shared scale moves ~600 call sites in one step, which is why this looked
 * fine in the token diff and wrong on screen.
 */
const radii = {
  none: '0',
  sm: '0.125rem', // 2px
  base: '0.25rem', // 4px
  md: '0.375rem', // 6px
  lg: '0.5rem', // 8px
  xl: '0.75rem', // 12px
  '2xl': '1rem', // 16px
  '3xl': '1.5rem', // 24px
  full: '9999px',
};

// Z-index scale for layering
const zIndices = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  submenu: 1550, // Submenus appear above parent dropdowns but below skip links
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

// Font weights
/**
 * Collapsed onto the two cuts Suisse Int'l is licensed in: 400 and 600.
 *
 * `medium` was 500, which has no licensed face — CSS font matching then serves
 * Regular, so every nav label, button, tab and form label silently lost its
 * emphasis step while still *claiming* to be medium. Pointing it at 600 keeps
 * the emphasis using a real drawn cut. Slightly heavier than a true Medium
 * would be; that is the honest trade until Suisse Int'l Medium is licensed,
 * at which point this becomes 500 again and nothing else changes.
 *
 * The weights above 600 collapse onto it as well. CSS font matching already
 * resolves a requested 700 to the real 600 face (verified in-browser: Suisse at
 * 600, 700 and 800 renders identical metrics), so these rows change nothing on
 * screen — they exist so the token map states the truth about what the app can
 * actually draw, instead of advertising four weights that are one.
 *
 * NOTE: this map only governs the NAMED tokens. A raw numeric `fontWeight: 500`
 * bypasses it entirely and matches the 400 face, which is the exact silent
 * downgrade described above. `typography.test.ts` fails on any weight — in this
 * map or in `textStyles` — that is not a licensed cut.
 */
const fontWeights = {
  hairline: 400,
  thin: 400,
  light: 400,
  normal: 400,
  medium: 600,
  semibold: 600,
  bold: 600,
  extrabold: 600,
  black: 600,
};

/**
 * The one light-mode grey ink, shared by `text.secondary`, `text.muted` and
 * `text.subtle`. They are deliberately the same value — see the `text.muted`
 * comment below — so this is a constant rather than three literals kept in step
 * by prose.
 *
 * `neutral.600`, the spec `--muted-foreground`. It clears AA on every resting
 * surface in the theme: 6.94:1 on white, 5.98 on the cream canvas, 5.61 on the
 * sidebar, 5.41 on `bg.subtle`, 5.05 on a hovered row. Pinned across all of
 * them by contrast.test.ts.
 */

/**
 * The dark-mode partner to GREY_INK, shared by `text.secondary`, `text.muted`
 * and `text.subtle` for the same reason the light one is shared. Lives in
 * `colors.brand.ts` with the rest of the derived dark palette — see the note
 * there on why it is #B4B9B4 rather than a straight read off the sand ramp.
 */

// Create the theme
const theme = extendTheme({
  config,
  breakpoints,
  space,
  shadows,
  borderWidths,
  radii,
  zIndices,
  colors,
  fonts,
  fontWeights,
  components,
  styles,
  // Type scale per Bower UI Style Guide. Reference via `<Text textStyle="body" />`.
  // All sizes in rem so users with browser font-scaling get proportional UI.
  // 1rem = 16px (the html default we don't override).
  textStyles: {
    // Headings run at 400, not 600: GT Ultra Median is licensed in Regular
    // only, and asking for 600 makes the browser synthesise a faux bold that
    // smears the stems of a high-contrast face. Tighter tracking and +2px on
    // h1/h2 buy back the hierarchy the lost weight would have carried.
    h1: {
      fontSize: '2rem', // 32px
      lineHeight: '2.375rem', // 38px
      fontWeight: 400,
      letterSpacing: '-0.025em',
      fontFamily: 'heading',
    },
    h2: {
      fontSize: '1.3125rem', // 21px
      lineHeight: '1.6875rem', // 27px
      fontWeight: 400,
      letterSpacing: '-0.02em',
      fontFamily: 'heading',
    },
    h3: {
      fontSize: '1.0625rem', // 17px
      lineHeight: '1.375rem', // 22px
      fontWeight: 400,
      letterSpacing: '-0.01em',
      fontFamily: 'heading',
    },
    body: {
      fontSize: '1rem', // 16px
      lineHeight: '1.6875rem', // 27px
      fontWeight: 400,
    },
    uiLabel: {
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25rem', // 20px
      // `medium` (600), not a raw 500: Suisse Int'l ships 400 and 600 only, so
      // a literal 500 matches the 400 face and this style renders identically
      // to `body`. Go through the token so the collapse above governs it.
      fontWeight: 'medium',
    },
    meta: {
      fontSize: '0.75rem', // 12px
      lineHeight: '1rem', // 16px
      fontWeight: 400,
    },
    eyebrow: {
      fontSize: '0.625rem', // 10px
      lineHeight: '0.875rem', // 14px
      fontWeight: 'medium', // see uiLabel — no 500 face exists
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
    },
    // Editorial families. Apply via `textStyle="display"` etc.
    display: {
      fontFamily: 'display',
      fontWeight: 400, // GT Ultra Median has no bold cut — see the h1 note above.
      letterSpacing: '-0.02em',
    },
    wordmark: {
      fontFamily: 'wordmark',
      fontWeight: 400,
    },
    // Numerics — GT Pressura Mono (since 2026-09), a true monospace, so
    // timestamps and counts align on advance width alone. `tnum` / `lnum` are
    // a no-op on it and on `ui-monospace` behind it; they are kept as cheap
    // insurance for an exotic fallback, not because anything in the current
    // stack needs them.
    //
    // NOTE this textStyle currently has NO production call sites — every real
    // numeric surface (RecordingTimer, VoiceToTextPopover, CountedPill,
    // ConfidenceBar, AuditTimelineEvent) sets `fontFamily="mono"` directly,
    // some adding `fontVariantNumeric` by hand. Either route them through here
    // or retire it; leaving it is how a token accumulates rationale nothing
    // exercises.
    numeric: {
      fontFamily: 'mono',
      fontFeatureSettings: '"tnum" 1, "lnum" 1',
      fontVariantNumeric: 'tabular-nums lining-nums',
    },
  },
  // Semantic tokens for consistent theming across light/dark modes
  semanticTokens,
});

export default theme;
